import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import axios from '../../utils/axios'; // Import the configured axios instance instead of the direct axios package
import AssessmentIcon from '@mui/icons-material/Assessment';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

const UsageReport = ({ adminInfo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCheckins, setTotalCheckins] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [universities, setUniversities] = useState([]);
  const [reportTrigger, setReportTrigger] = useState(0);
  const [currentUniversityName, setCurrentUniversityName] = useState('All Universities');
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [universityBreakdown, setUniversityBreakdown] = useState([]);

  useEffect(() => {
    const fetchUsageData = async () => {
      setLoading(true);
      try {
        // Use the api path directly without adding a base URL since the axios instance already has it configured
        let url = `/api/admin/usage-report/?university=${selectedUniversity}`;
        
        if (startDate) {
          url += `&start_date=${startDate}`;
        }
        
        if (endDate) {
          url += `&end_date=${endDate}`;
        }
        
        const response = await axios.get(url);
        const data = response.data;
        
        // Add default values of 0 if the data doesn't contain these properties
        setTotalCheckins(data.totalCheckins || 0);
        setTotalEvents(data.totalEvents || 0);
        
        // Update universities dropdown if it's included in the response
        if (data.universities && data.universities.length > 0) {
          setUniversities(data.universities);
          
          // Find the name of the current selected university
          const selectedUni = data.universities.find(uni => uni.id === selectedUniversity);
          if (selectedUni) {
            setCurrentUniversityName(selectedUni.name);
          } else {
            setCurrentUniversityName('All Universities');
          }
        }
        
        // Store university breakdown data if available
        if (data.universityBreakdown) {
          setUniversityBreakdown(data.universityBreakdown);
        } else {
          setUniversityBreakdown([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to load usage report data. Please try again later.');
        setLoading(false);
      }
    };

    fetchUsageData();
  }, [reportTrigger]); // Only depend on reportTrigger instead of filter values

  const handleUniversityChange = (event) => {
    setSelectedUniversity(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleSubmitReport = () => {
    setLoading(true);
    setReportTrigger(prev => prev + 1);
  };

  const handleDownloadReport = () => {
    setDownloadingReport(true);
    try {
      // Prepare the CSV content
      const dateOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
      const currentDate = new Date().toLocaleDateString('en-US', dateOptions);
      
      // Build the CSV header and data rows
      let csvContent = "Report Date,University,Start Date,End Date,Total Events,Total Check-ins\n";
      csvContent += `${currentDate},${currentUniversityName},`;
      csvContent += `${startDate || 'Not specified'},`;
      csvContent += `${endDate || 'Not specified'},`;
      csvContent += `${totalEvents},${totalCheckins}\n\n`;
      
      // Add a detailed section header
      csvContent += "Attendance Report Details\n";
      csvContent += "Generated on," + new Date().toLocaleString() + "\n";
      csvContent += "Filter: University," + currentUniversityName + "\n";
      csvContent += "Filter: Date Range," + (startDate ? `From ${startDate}` : 'All time') + 
                    (endDate ? ` To ${endDate}` : '') + "\n";
      csvContent += "Total Events," + totalEvents + "\n";
      csvContent += "Total Check-ins," + totalCheckins + "\n";
      
      // If we have university breakdown data, include it in the CSV
      if (selectedUniversity === 'all' && universityBreakdown && universityBreakdown.length > 0) {
        csvContent += "\nBreakdown by University\n";
        csvContent += "University Name,Total Events,Total Check-ins\n";
        
        universityBreakdown.forEach(uni => {
          csvContent += `${uni.name},${uni.events},${uni.checkins}\n`;
        });
      }
      
      // Universal download method that works across all browsers
      const downloadCsv = (content, filename) => {
        try {
          // Create a blob with the CSV content
          const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
          
          // Create a download link
          const link = document.createElement('a');
          
          // Set link properties
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.style.display = 'none';
          
          // Add to document, trigger click, and clean up
          document.body.appendChild(link);
          link.click();
          
          // Clean up after a short delay to ensure the download starts
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          }, 100);
          
          return true;
        } catch (err) {
          console.error('Error in downloadCsv:', err);
          return false;
        }
      };
      
      // Generate a filename with the current date
      const filename = `attendance_report_${currentDate.replace(/\//g, '-')}.csv`;
      const downloadSuccess = downloadCsv(csvContent, filename);
      
      // If download fails, offer alternative method
      if (!downloadSuccess) {
        console.warn('Standard download failed, trying data URI method');
        
        try {
          // Create a data URI and open it in a new tab/window
          const encodedData = encodeURIComponent(csvContent);
          const dataUri = `data:text/csv;charset=utf-8,${encodedData}`;
          
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(`
              <html>
                <head>
                  <title>Download Attendance Report</title>
                </head>
                <body>
                  <h3>Your download should begin automatically.</h3>
                  <p>If not, please right-click anywhere on this page and select "Save As" to download the CSV file.</p>
                  <p>You can also copy the content below and paste it into a text editor, then save it as a CSV file.</p>
                  <textarea style="width: 100%; height: 300px;">${csvContent}</textarea>
                </body>
              </html>
            `);
            
            // Also try to trigger download in the new window
            const newLink = newWindow.document.createElement('a');
            newLink.href = dataUri;
            newLink.download = filename;
            newLink.click();
          } else {
            // If popup is blocked, show an alert
            alert('Please allow popups for this site to download the attendance report, or contact support for assistance.');
          }
        } catch (fallbackErr) {
          console.error('Fallback download method failed:', fallbackErr);
          alert('Unable to download the attendance report. Please try a different browser or contact support.');
        }
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report. Please try again later.');
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress sx={{ color: '#DEA514' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%',
      p: 3,
      backgroundColor: '#f8f8f8',
      minHeight: '90vh'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4, 
        borderBottom: '2px solid #DEA514',
        paddingBottom: 2 
      }}>
        <AssessmentIcon sx={{ fontSize: 40, color: '#DEA514', mr: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Usage Report
        </Typography>
      </Box>
      
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 2,
          backgroundColor: '#ffffff'
        }}
      >
        <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" sx={{ mb: { xs: 2, md: 0 } }}>
              <InputLabel id="university-select-label">University</InputLabel>
              <Select
                labelId="university-select-label"
                id="university-select"
                value={selectedUniversity}
                label="University"
                onChange={handleUniversityChange}
                sx={{ 
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.15)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#DEA514',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#DEA514',
                  },
                }}
              >
                {universities.map((uni) => (
                  <MenuItem key={uni.id} value={uni.id}>{uni.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              id="start-date"
              label="Start Date"
              type="date"
              fullWidth
              variant="outlined"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ 
                mb: { xs: 2, md: 0 },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#DEA514',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#DEA514',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              id="end-date"
              label="End Date"
              type="date"
              fullWidth
              variant="outlined"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ 
                mb: { xs: 2, md: 0 },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#DEA514',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#DEA514',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleSubmitReport}
              startIcon={<SearchIcon />}
              sx={{ 
                py: 1,
                height: '100%',
                maxHeight: '56px',
                backgroundColor: '#DEA514',
                '&:hover': {
                  backgroundColor: '#C08300',
                },
                boxShadow: '0 2px 4px rgba(222, 165, 20, 0.2)',
              }}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#555' }}>
            Report Summary
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={downloadingReport}
            sx={{
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                borderColor: '#388E3C',
                backgroundColor: 'rgba(76, 175, 80, 0.04)',
              },
            }}
          >
            {downloadingReport ? 'Downloading...' : 'Download Report'}
          </Button>
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(222, 165, 20, 0.4)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px -10px rgba(222, 165, 20, 0.4)'
                } 
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventIcon sx={{ fontSize: 36, mr: 2, color: '#DEA514' }} />
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Total Events
                  </Typography>
                </Box>
                <Typography variant="h2" component="div" sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center', 
                  my: 2,
                  color: '#DEA514'
                }}>
                  {(totalEvents !== undefined && totalEvents !== null) ? totalEvents.toLocaleString() : '0'}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#666', textAlign: 'center' }}>
                  Events organized based on your filters
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(222, 165, 20, 0.4)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px -10px rgba(222, 165, 20, 0.4)'
                } 
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HowToRegIcon sx={{ fontSize: 36, mr: 2, color: '#DEA514' }} />
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Total Check-ins
                  </Typography>
                </Box>
                <Typography variant="h2" component="div" sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center', 
                  my: 2,
                  color: '#DEA514'
                }}>
                  {(totalCheckins !== undefined && totalCheckins !== null) ? totalCheckins.toLocaleString() : '0'}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#666', textAlign: 'center' }}>
                  Attendance records based on your filters
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* University breakdown table - only show when "All Universities" is selected */}
        {selectedUniversity === 'all' && universityBreakdown && universityBreakdown.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#555', mb: 2 }}>
              Breakdown by University
            </Typography>
            
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.05)' }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>University Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Events</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Check-ins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {universityBreakdown.map((university) => (
                    <TableRow 
                      key={university.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'rgba(222, 165, 20, 0.04)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SchoolIcon sx={{ fontSize: 20, color: '#DEA514', mr: 1 }} />
                          {university.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{university.events.toLocaleString()}</TableCell>
                      <TableCell align="right">{university.checkins.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UsageReport;
